import { v4 } from 'uuid'

import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiNews } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { isSupervisedSelector } from './session'

const newsSlice = createReduxSlice<{ hash: string }>('news')
const newsSyncSlice = createReduxSlice<{ hash: string }>('news_sync')

const newsStateSlice = createReduxSlice<{ loadedAt: number; news: ApiNews[]; next: string | null }>('news_state')

const markNewsReadSlice = () =>
  newsStateSlice.update(state => {
    if (!state) return state
    let changed = false
    const models = state.news.map(m => {
      if (m.attributes.read) return m
      changed = true
      return { ...m, attributes: { ...m.attributes, read: true } }
    })
    if (!changed) return state
    return { ...state, news: models }
  })
const newsLoadingSlice = createReduxSlice<{ loading: boolean }>('news_loading')

export const unsetNewsSync = () => newsSyncSlice.set({ hash: v4() })

export const newsResultSelector = createSelector(
  [newsSlice.selector, newsSyncSlice.selector, newsStateSlice.selector, profileUserResultSelector],
  (meta, sync, state, userResult) =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (!user || user.account_type === 'visitor') return []
      const hash = [user.id, sync?.hash || ''].join(':')
      if (!meta || meta.hash !== hash) {
        return c.load(
          createPromise('News must be fetched', hash, async dispatch => {
            await dispatch(fetchInitialNews())
            dispatch(newsSlice.set({ hash }))
          })
        )
      }
      if (!state) throw new Error('News state is missing')
      return state.news
    })
)
export const newsUnreadCountResultSelector = mapThunkSelector(newsResultSelector, articles => {
  return articles.filter(a => !a.attributes.read).length
})

export const newsHasMoreSelector = createSelector([newsStateSlice.selector], meta => {
  if (!meta) return false
  return meta.next !== null
})
export const newsLoadingSelector = createSelector([newsLoadingSlice.selector], meta => {
  if (!meta) return false
  return meta.loading
})

export const fetchInitialNews = () =>
  createThunk(async dispatch => {
    const [{ getNews }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/news'), import('@app/store/actions/api')])
    using stack = new DisposableStack()
    dispatch(newsLoadingSlice.set({ loading: true }))
    stack.defer(() => {
      dispatch(newsLoadingSlice.set({ loading: false }))
    })
    const resp = await dispatch(callV2Api(getNews({ page: 1, per_page: 10 }))).catch(catchFetchDataError(dispatch, 'News fetch failed'))
    dispatch(
      newsStateSlice.set({
        loadedAt: moment().unix(),
        news: resp.data,
        next: resp.data.length < 10 ? null : (resp.data.at(-1)?.attributes.published_at ?? null),
      })
    )
    return resp
  })

export function fetchNextNews() {
  return createThunk(async dispatch => {
    const [{ getNews }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/news'), import('@app/store/actions/api')])
    using stack = new DisposableStack()
    const state = dispatch(newsStateSlice.get())
    if (dispatch(newsLoadingSlice.get())?.loading) return
    if (!state) return
    if (state.next === null) return
    dispatch(newsLoadingSlice.set({ loading: true }))
    stack.defer(() => {
      dispatch(newsLoadingSlice.set({ loading: false }))
    })
    const resp = await dispatch(callV2Api(getNews({ before: moment(state.next).unix(), per_page: 10 }))).catch(
      catchFetchDataError(dispatch, 'News fetch failed')
    )
    dispatch(
      newsStateSlice.set({
        ...state,
        news: [...state.news, ...resp.data],
        next: resp.data.length < 10 ? null : (resp.data.at(-1)?.attributes.published_at ?? null),
      })
    )
    return resp
  })
}

export function newsMarkAsRead() {
  return createThunk(async (dispatch, getState) => {
    // supervise mode marks read locally, without telling the server
    if (isSupervisedSelector(getState())) {
      dispatch(markNewsReadSlice())
      return
    }

    const [{ postNewsRead }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/news'), import('@app/store/actions/api')])
    await dispatch(callV2Api(postNewsRead()))
    dispatch(markNewsReadSlice())
  })
}
